import type { LoadingItems } from '@/constants/LoadingItems';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { useIsPdf } from './usePdf';
import { LogLevel } from '@grafana/faro-web-sdk';

const DEBUG = false;

export function useIsSystemLoading(): ComputedRef<boolean> {
  const isPdf = useIsPdf();

  return computed(() => loadingItems.value.length > 0 && !isPdf.value);
}

/**
 * Because there are often multiple things loading at the same time, we keep track
 * of just their names here. If loadingItems has any items, then we should show
 * a page loading indicator. If these are smaller items, like charts, then we
 * should have a separate loading indicator. But we don't want to show a chart loading
 * indicator behind the page loading indicator, so please manage that on the chart
 * yourself :)
 */
const loadingItems = ref<LoadingItems[]>([]);

/**
 * this removes one item from the array of things loading
 * @param item name of the item that has stopped loading
 */
const removeLoadingItem: (item: LoadingItems) => void = (item: LoadingItems): void => {
  const copy = loadingItems.value.slice();
  const idx = copy.indexOf(item);

  if (idx >= 0) {
    performance.mark(`${item}-end`);
    const result = performance.measure(item, `${item}-start`, `${item}-end`);

    copy.splice(idx, 1);

    // Send loading duration metric to Grafana Faro
    // Import lazily to avoid circular dependency
    if (result) {
      import('@/plugins/faro')
        .then(({ faroInstance }) => {
          if (faroInstance) {
            try {
              faroInstance.api.pushMeasurement(
                {
                  type: 'loading_duration',
                  values: {
                    duration_ms: result.duration,
                  },
                },
                {
                  context: {
                    loadingItem: item,
                  },
                },
              );
            } catch (error) {
              // Silently fail - telemetry should not break loading functionality
              if (DEBUG) console.error('Failed to send loading duration to Grafana Faro:', error);
            }
          }
        })
        .catch(() => {
          // Silently fail - telemetry should not break loading functionality
        });
    }

    if (DEBUG) {
      console.info(`removed loading item ${item.toUpperCase()}, loading items is now`, copy);
      console.info(`${item.toUpperCase()} took ${result.duration}`);
    }
    loadingItems.value = copy;
  }
};

/**
 * this adds one item from the array of things loading
 * @param item name of the item that has started loading
 */
const addLoadingItem: (item: LoadingItems) => void = (item: LoadingItems): void => {
  const copy = loadingItems.value.slice();

  if (!copy.includes(item)) {
    copy.push(item);
    performance.mark(`${item}-start`);

    // Log loading start to Grafana Faro for observability
    // Import lazily to avoid circular dependency
    import('@/plugins/faro')
      .then(({ faroInstance }) => {
        if (faroInstance) {
          try {
            faroInstance.api.pushLog([`Loading started: ${item}`], {
              level: LogLevel.DEBUG,
              context: {
                loadingItem: item,
                action: 'start',
              },
            });
          } catch (error) {
            // Silently fail - telemetry should not break loading functionality
            if (DEBUG) console.error('Failed to log loading start to Grafana Faro:', error);
          }
        }
      })
      .catch(() => {
        // Silently fail - telemetry should not break loading functionality
      });

    if (DEBUG) console.info(`added loading item ${item.toUpperCase()}, loading items is now`, copy);
    loadingItems.value = copy;
  }
};

export default function (): {
  addLoadingItem: (item: LoadingItems) => void;
  removeLoadingItem: (item: LoadingItems) => void;
  loadingItems: Ref<LoadingItems[]>;
} {
  return {
    addLoadingItem,
    removeLoadingItem,
    loadingItems,
  };
}
